import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Save, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function SiteContentManager() {
  const [content, setContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/admin/site-content`);
      setContent(res.data.content);
    } catch (error) {
      toast.error('Failed to load site content');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchContent(); }, []);

  const handleChange = (key, field, value) => {
    setContent(prev => prev.map(item => 
      item.key === key ? { ...item, [field]: value } : item
    ));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/site-content`, { content });
      toast.success('Site content saved successfully!');
    } catch (error) {
      toast.error('Failed to save site content');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredContent = content.filter(item => 
    item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group content by section - Only show footer and cookies (other sections are managed separately)
  const allowedSections = ['footer', 'cookies'];
  const groupedContent = filteredContent.reduce((acc, item) => {
    const section = item.key.split('.')[0];
    if (!allowedSections.includes(section)) return acc;
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  const sectionLabels = {
    footer: 'Footer',
    cookies: 'Cookie Consent'
  };

  const sectionIcons = {
    footer: '📋',
    cookies: '🍪'
  };

  if (isLoading) {
    return <div className="text-center py-12"><div className="animate-spin w-8 h-8 border-4 border-[#C41E3A] border-t-transparent rounded-full mx-auto"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Site Content</h2>
          <p className="text-sm text-slate-500">Edit footer and cookie consent text in all languages</p>
        </div>
        <Button onClick={handleSave} disabled={isSaving} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
          <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input 
          placeholder="Search content..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-full"
        />
      </div>

      {/* Content Sections */}
      <div className="space-y-6">
        {Object.entries(groupedContent).map(([section, items]) => (
          <Card key={section} className="bg-white border-slate-100 rounded-3xl">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span>{sectionIcons[section]}</span> {sectionLabels[section] || section}
              </h3>
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.key} className="p-4 bg-slate-50 rounded-2xl">
                    <Label className="text-sm text-slate-600 mb-2 block">{item.label}</Label>
                    <code className="text-xs text-slate-400 mb-3 block">{item.key}</code>
                    
                    <Tabs defaultValue="en" className="w-full">
                      <TabsList className="grid w-full grid-cols-3 mb-3">
                        {LANGUAGES.map(lang => (
                          <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                            {lang.flag} {lang.name}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      
                      {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code}>
                          <div className={`p-3 rounded-xl ${lang.bg}`}>
                            {(item[`value_${lang.code}`] && item[`value_${lang.code}`].length > 100) || 
                             (item.value_en && item.value_en.length > 100) ? (
                              <Textarea 
                                value={item[`value_${lang.code}`] || ''}
                                onChange={(e) => handleChange(item.key, `value_${lang.code}`, e.target.value)}
                                className="bg-white rounded-xl text-sm"
                                rows={3}
                                placeholder={lang.code === 'en' ? '' : `${lang.name} translation...`}
                              />
                            ) : (
                              <Input 
                                value={item[`value_${lang.code}`] || ''}
                                onChange={(e) => handleChange(item.key, `value_${lang.code}`, e.target.value)}
                                className="bg-white rounded-xl text-sm"
                                placeholder={lang.code === 'en' ? '' : `${lang.name} translation...`}
                              />
                            )}
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {Object.keys(groupedContent).length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No content items found matching your search.
          </div>
        )}
      </div>

      {/* Save Button at bottom */}
      <div className="flex justify-end pt-4">
        <Button onClick={handleSave} disabled={isSaving} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full px-8">
          <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
}
